import React from "react";
import { Icon, Stack, Text } from "@bookingcom/bui-react";
import FlightDetailsAncillaryItemLayout from "../FlightDetailsAncillaryItemLayout";
import { LoadingAncillariesPrice } from "../../../ancillaries/components/ExtraProducts/components/LoadingAncillariesPrice";
import { SVG } from "@bookingcom/bui-react/components/Icon/Icon.types";
import { UIAvailableExtraProducts, UICampaignBadge, UIConfidenceLevel } from "@flights/types";
import FlexTicketRecommendedBadge from "../../../ancillaries/components/TicketTypeSelector/FlexibleTicket/FlexTicketRecommendedBadge";
import styles from "./FlightDetailsAncillaryItem.module.css";
import { getCampaignBadgesConfig } from "../../../utils/flightCampaignConfig";
import { FlightCardBadge } from "../FlightCard/components/FlightCardBadge";
import flights_apex_web_flex_rec_on_fd_v2 from "../../../utils/experiments/apex/flights_apex_web_flex_rec_on_fd_v2";
import FlightDetailsAncillaryItemLayoutAlt from "../FlightDetailsAncillaryItemLayoutAlt";
import useUserAgent from "../../../hooks/useUserAgent";

type Props = {
  icon: SVG;
  priceCopy: string;
  title: string;
  description: string;
  showLoadingPrice?: boolean;
  type: keyof UIAvailableExtraProducts;
  priceStrikethrough?: string;
  priceStrikethroughA11yText?: string;
  isRecommended?: boolean;
  confidenceLevel?: UIConfidenceLevel;
  geniusDealBadges?: UICampaignBadge[];
  /* start - flights_web_condensed_extras_fd_ddot, flights_web_condensed_extras_fd_mdot */
  condensedFDDesktop?: boolean;
  condensedFDMobile?: boolean;
  /* end - flights_web_condensed_extras_fd_ddot, flights_web_condensed_extras_fd_mdot */
};

const FlightDetailsAncillaryItem = ({
  showLoadingPrice,
  icon,
  title,
  description,
  priceCopy,
  type,
  priceStrikethrough,
  priceStrikethroughA11yText,
  isRecommended,
  confidenceLevel,
  geniusDealBadges,
  /* start - flights_web_condensed_extras_fd_ddot, flights_web_condensed_extras_fd_mdot */
  condensedFDDesktop,
  condensedFDMobile
}: /* end - flights_web_condensed_extras_fd_ddot, flights_web_condensed_extras_fd_mdot */
Props) => {
  const { isMobile } = useUserAgent();

  const canShowFlexRecommendationBadge =
    type === "flexibleTicket" &&
    isRecommended &&
    (confidenceLevel === "MEDIUM" || confidenceLevel === "HIGH" || confidenceLevel === "VERY_HIGH");

  const geniusDealAncillaryBadges = getCampaignBadgesConfig(geniusDealBadges).filter(
    (badge) => badge.style !== "GENIUS"
  );
  /* start - flights_apex_web_flex_rec_on_fd */
  const showFlexRecommendationBadge =
    canShowFlexRecommendationBadge && !!flights_apex_web_flex_rec_on_fd_v2.trackWithDefaultStage();

  if (canShowFlexRecommendationBadge) {
    flights_apex_web_flex_rec_on_fd_v2.trackStages(isMobile, confidenceLevel);
  }
  /* end - flights_apex_web_flex_rec_on_fd */

  /* start - flights_web_condensed_extras_fd_ddot, flights_web_condensed_extras_fd_mdot */
  return condensedFDDesktop || condensedFDMobile ? (
    <FlightDetailsAncillaryItemLayoutAlt
      icon={<Icon svg={icon} size="large" />}
      recommendation={
        !(geniusDealAncillaryBadges?.length > 0) && showFlexRecommendationBadge && <FlexTicketRecommendedBadge />
      }
      leftColumnContent={
        <div>
          <Stack direction="row" gap={condensedFDDesktop ? 1 : undefined}>
            <Text variant="body_2">{title}</Text>
            {geniusDealAncillaryBadges?.length > 0 && (
              <div className={condensedFDDesktop ? null : styles.badges}>
                {geniusDealAncillaryBadges?.map((badge, idx) => (
                  <FlightCardBadge key={`${badge.style}-${badge.text || idx}`} {...badge} />
                ))}
                {showFlexRecommendationBadge && <FlexTicketRecommendedBadge />}
              </div>
            )}
          </Stack>
          {!condensedFDMobile && <Text variant="body_2">{description}</Text>}
        </div>
      }
      rightColumnContent={
        showLoadingPrice ? (
          <LoadingAncillariesPrice price={priceCopy} type={type} />
        ) : (
          <Stack
            direction={condensedFDMobile ? "column" : "row"}
            alignItems={condensedFDMobile ? "end" : "center"}
            gap={condensedFDDesktop ? 1 : undefined}
            wrap="nowrap"
            attributes={!!priceStrikethroughA11yText ? { "aria-label": priceStrikethroughA11yText } : {}}
          >
            {priceStrikethrough && (
              <Text
                variant={condensedFDMobile ? "small_1" : "body_2"}
                className={styles.priceStrikethrough}
                attributes={{ "aria-hidden": !!priceStrikethroughA11yText }}
              >
                {priceStrikethrough}
              </Text>
            )}
            <Text
              variant={condensedFDMobile ? "small_1" : "body_2"}
              attributes={{ "aria-hidden": !!priceStrikethroughA11yText }}
            >
              {priceCopy}
            </Text>
          </Stack>
        )
      }
      alignIconCenter={false}
    />
  ) : (
    /* end - flights_web_condensed_extras_fd_ddot, flights_web_condensed_extras_fd_mdot */
    <FlightDetailsAncillaryItemLayout
      icon={<Icon svg={icon} size="large" />}
      recommendation={
        !(geniusDealAncillaryBadges?.length > 0) && showFlexRecommendationBadge && <FlexTicketRecommendedBadge />
      }
      content={
        <div>
          <Stack direction="row">
            <Text variant="body_2">{title}</Text>
            {geniusDealAncillaryBadges?.length > 0 && (
              <>
                <div className={styles.badges}>
                  {geniusDealAncillaryBadges?.map((badge, idx) => (
                    <FlightCardBadge key={`${badge.style}-${badge.text || idx}`} {...badge} />
                  ))}
                </div>
                <div className={styles.badges}>{showFlexRecommendationBadge && <FlexTicketRecommendedBadge />}</div>
              </>
            )}
          </Stack>
          <Text variant="body_2">{description}</Text>
          {showLoadingPrice ? (
            <LoadingAncillariesPrice price={priceCopy} type={type} />
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              wrap="nowrap"
              attributes={!!priceStrikethroughA11yText ? { "aria-label": priceStrikethroughA11yText } : {}}
            >
              {priceStrikethrough && (
                <Text
                  variant="body_2"
                  className={styles.priceStrikethrough}
                  attributes={{ "aria-hidden": !!priceStrikethroughA11yText }}
                >
                  {priceStrikethrough}
                </Text>
              )}
              <Text variant="body_2" attributes={{ "aria-hidden": !!priceStrikethroughA11yText }}>
                {priceCopy}
              </Text>
            </Stack>
          )}
        </div>
      }
      alignIconCenter={false}
      condenseExtras={condensedFDMobile}
    />
  );
};

export default FlightDetailsAncillaryItem;
